import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

export enum AeoOnboardingRunStatus {
  RUNNING = "running",
  COMPLETED = "completed",
  FAILED = "failed",
}
export const AeoOnboardingRunStatusSchema = createEnum(AeoOnboardingRunStatus);

export enum AeoOnboardingStepKey {
  GENERATE_BRAND_IDENTITY_GUIDE = "generate_brand_identity_guide",
  GENERATE_PROMPTS = "generate_prompts",
  SUBMIT_BRIGHTDATA = "submit_brightdata",
  INGEST_BRIGHTDATA = "ingest_brightdata",
  DISCOVER_COMPETITORS = "discover_competitors",
  EXTRACT_MENTIONS = "extract_mentions",
}
export const AeoOnboardingStepKeySchema = createEnum(AeoOnboardingStepKey);

export enum AeoOnboardingStepStatus {
  NOT_STARTED = "not_started",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  FAILED = "failed",
}
export const AeoOnboardingStepStatusSchema = createEnum(
  AeoOnboardingStepStatus,
);

export enum AeoOnboardingErrorCode {
  AEO_NOT_ENABLED = "AEO_NOT_ENABLED",
  BIG_GENERATION_FAILED = "BIG_GENERATION_FAILED",
  PROMPT_GENERATION_FAILED = "PROMPT_GENERATION_FAILED",
  BRIGHTDATA_SUBMIT_FAILED = "BRIGHTDATA_SUBMIT_FAILED",
  BRIGHTDATA_SUBMIT_PARTIAL = "BRIGHTDATA_SUBMIT_PARTIAL",
  BRIGHTDATA_POLL_TIMEOUT = "BRIGHTDATA_POLL_TIMEOUT",
  BRIGHTDATA_INGEST_STALLED = "BRIGHTDATA_INGEST_STALLED",
  BRIGHTDATA_INGEST_TIMEOUT = "BRIGHTDATA_INGEST_TIMEOUT",
  BRIGHTDATA_RUN_FAILED = "BRIGHTDATA_RUN_FAILED",
  COMPETITOR_DISCOVERY_FAILED = "COMPETITOR_DISCOVERY_FAILED",
  MENTION_EXTRACTION_FAILED = "MENTION_EXTRACTION_FAILED",
  TEMPORAL_START_FAILED = "TEMPORAL_START_FAILED",
  INTERNAL_ERROR = "INTERNAL_ERROR",
}
export const AeoOnboardingErrorCodeSchema = createEnum(AeoOnboardingErrorCode);

export enum AeoOnboardingPhaseKind {
  ANALYZING = "analyzing",
  COLLECTING = "collecting",
  PREPARING = "preparing",
}
export const AeoOnboardingPhaseKindSchema = createEnum(AeoOnboardingPhaseKind);

export const AEO_ONBOARDING_STEPS: {
  key: AeoOnboardingStepKey;
  phase: AeoOnboardingPhaseKind;
}[] = [
  {
    key: AeoOnboardingStepKey.GENERATE_BRAND_IDENTITY_GUIDE,
    phase: AeoOnboardingPhaseKind.ANALYZING,
  },
  {
    key: AeoOnboardingStepKey.GENERATE_PROMPTS,
    phase: AeoOnboardingPhaseKind.ANALYZING,
  },
  {
    key: AeoOnboardingStepKey.SUBMIT_BRIGHTDATA,
    phase: AeoOnboardingPhaseKind.COLLECTING,
  },
  {
    key: AeoOnboardingStepKey.INGEST_BRIGHTDATA,
    phase: AeoOnboardingPhaseKind.COLLECTING,
  },
  {
    key: AeoOnboardingStepKey.DISCOVER_COMPETITORS,
    phase: AeoOnboardingPhaseKind.PREPARING,
  },
  {
    key: AeoOnboardingStepKey.EXTRACT_MENTIONS,
    phase: AeoOnboardingPhaseKind.PREPARING,
  },
] as const;

export enum AeoOnboardingWorkflowStatus {
  RUNNING = "running",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
  NOT_FOUND = "not_found",
}
export const AeoOnboardingWorkflowStatusSchema = createEnum(
  AeoOnboardingWorkflowStatus,
);

const AeoOnboardingRunSchema_ = z.object({
  id: z.string(),
  accountId: z.string(),
  status: AeoOnboardingRunStatusSchema,
  startedAt: z.date(),
  completedAt: z.date().nullable(),
  updatedAt: z.date(),
  workflowId: z.string().nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
});
export const AeoOnboardingRunSchema: z.ZodType<AeoOnboardingRun> =
  AeoOnboardingRunSchema_;
export interface AeoOnboardingRun
  extends z.infer<typeof AeoOnboardingRunSchema_> {}

const AeoOnboardingStepSchema_ = z.object({
  id: z.string(),
  runId: z.string(),
  stepKey: AeoOnboardingStepKeySchema,
  title: z.string(),
  status: AeoOnboardingStepStatusSchema,
  stepOrder: z.number(),
  startedAt: z.date().nullable(),
  completedAt: z.date().nullable(),
  updatedAt: z.date(),
  attemptCount: z.number(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
});
export const AeoOnboardingStepSchema: z.ZodType<AeoOnboardingStep> =
  AeoOnboardingStepSchema_;
export interface AeoOnboardingStep
  extends z.infer<typeof AeoOnboardingStepSchema_> {}
